import { Injectable } from '@angular/core';
import { jsPDF } from 'jspdf';
import type { Matrix } from 'jspdf';
import { NzIconService } from 'ng-zorro-antd/icon';
import { firstValueFrom } from 'rxjs';
import {
  QrPosterBlock,
  QrPosterRenderContext,
  QrPosterTextFit,
  QrPosterTemplate,
  normalizeQrPosterTextFit,
} from '../models/qr-poster-template.model';
import { getQrPosterIconDefinition, getQrPosterPreviewIconType } from '../models/qr-poster-icons.model';
import { QrPosterVariableResolverService } from './qr-poster-variable-resolver.service';

interface QrPosterTextRenderOptions {
  pdf: jsPDF;
  template: QrPosterTemplate;
  context: QrPosterRenderContext;
  language: string;
  enforceScanafyFooter: boolean;
  footerEnabled?: boolean;
  debugTextRendering?: boolean;
  renderSource?: string;
}

interface QrPosterTextLayoutMetrics {
  textFit: QrPosterTextFit;
  fontSizeOriginal: number;
  fontSizeFinal: number;
  fontWeight: 'normal' | 'bold';
  align: 'left' | 'center' | 'right';
  horizontalPadding: number;
  verticalPadding: number;
  availableWidth: number;
  availableHeight: number;
  pdfFontFamily: string;
  textWidthOriginal: number;
  textWidthFinal: number;
  splitLinesOriginal: string[];
  splitLinesFinal: string[];
  lineCountOriginal: number;
  lineCountFinal: number;
  maxLines: number;
  didShrink: boolean;
  didClip: boolean;
  clippedLines: string[];
}

interface ParsedPdfColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

@Injectable({ providedIn: 'root' })
export class QrPosterTemplateRendererService {
  private static readonly MIN_TEXT_FIT_FONT_SIZE_PT = 6;
  private regularFontBase64?: string;
  private boldFontBase64?: string;
  private fontsReady = false;
  private fontLoadingPromise: Promise<void> | null = null;
  private readonly iconPngCache = new Map<string, string | null>();
  private readonly debugQrPosterTextRendering = false;

  constructor(
    private readonly variableResolver: QrPosterVariableResolverService,
    private readonly iconService: NzIconService,
  ) {}

  async renderTemplate(options: QrPosterTextRenderOptions): Promise<void> {
    const { pdf, template } = options;
    await this.ensureFonts(pdf);
    const iconDataUrls = await this.preloadTemplateIcons(template, options.context);

    const blocks = [...(template.blocks || [])]
      .filter((block) => !block.hidden && this.variableResolver.shouldRenderTemplateBlock(block, options.context, template.blocks))
      .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));

    for (const block of blocks) {
      await this.renderBlock(block, options, iconDataUrls);
    }

    const footerEnabled = options.footerEnabled
      ?? (template.branding?.showScanafyFooter || options.enforceScanafyFooter);
    const hasFooterBlock = blocks.some((block) => block.type === 'footer');
    const shouldAutoFooter = !hasFooterBlock && footerEnabled;
    if (shouldAutoFooter) {
      this.drawFooter(pdf, 0, 195, 148, 15);
    }
  }

  private async renderBlock(
    block: QrPosterBlock,
    options: QrPosterTextRenderOptions,
    iconDataUrls: Map<string, string | null>
  ): Promise<void> {
    await this.withBlockRotation(options.pdf, block, async () => {
      switch (block.type) {
        case 'text':
        case 'variableText':
          this.drawTextBlock(block, options);
          return;
        case 'image':
          await this.drawImageBlock(block, options.pdf, block.content?.imageUrl || '', block.content?.fit || 'cover');
          return;
        case 'icon':
          await this.drawIconBlock(block, options.pdf, iconDataUrls);
          return;
        case 'companyLogo':
          await this.drawImageBlock(block, options.pdf, options.context.companyLogoBase64 || 'assets/scanafy_company_logo_croped.png', 'contain');
          return;
        case 'qrCode':
          await this.drawImageBlock(block, options.pdf, options.context.qrDataUrl, 'contain');
          return;
        case 'line':
          this.drawLineBlock(block, options.pdf);
          return;
        case 'rectangle':
          this.drawRectangleBlock(block, options.pdf);
          return;
        case 'iconText':
          this.drawIconTextBlock(block, options, iconDataUrls);
          return;
        case 'iconMultilineText':
          this.drawIconMultilineTextBlock(block, options, iconDataUrls);
          return;
        case 'emergencyNumbers':
          this.drawEmergencyNumbersBlock(block, options);
          return;
        case 'footer':
          if (options.footerEnabled
            ?? (options.template.branding?.showScanafyFooter || options.enforceScanafyFooter)) {
            this.drawFooter(options.pdf, block.x, block.y, block.width, block.height, block);
          }
          return;
        default:
          return;
      }
    });
  }

  private async fetchFontAsBase64(url: string): Promise<string> {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to load font: ${url}`);
    }

    const buffer = await response.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    let binary = '';

    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }

    return btoa(binary);
  }

  private async ensureFonts(pdf: jsPDF): Promise<void> {
    if (!this.fontsReady) {
      try {
        if (!this.fontLoadingPromise) {
          this.fontLoadingPromise = Promise.all([
            this.regularFontBase64
              ? Promise.resolve(this.regularFontBase64)
              : this.fetchFontAsBase64('/assets/fonts/OpenSans-Regular.ttf'),
            this.boldFontBase64
              ? Promise.resolve(this.boldFontBase64)
              : this.fetchFontAsBase64('/assets/fonts/OpenSans-Bold.ttf'),
          ]).then(([regularFontBase64, boldFontBase64]) => {
            this.regularFontBase64 = regularFontBase64;
            this.boldFontBase64 = boldFontBase64;
            this.fontsReady = true;
          });
        }
        await this.fontLoadingPromise;
      } catch (error) {
        console.warn('QR poster renderer font preload failed; falling back to Helvetica.', error);
        this.fontsReady = false;
      } finally {
        if (!this.fontsReady) {
          this.fontLoadingPromise = null;
        }
      }
    }

    if (this.fontsReady && this.regularFontBase64 && this.boldFontBase64) {
      pdf.addFileToVFS('OpenSans-Regular.ttf', this.regularFontBase64);
      pdf.addFont('OpenSans-Regular.ttf', 'OpenSans', 'normal');
      pdf.addFileToVFS('OpenSans-Bold.ttf', this.boldFontBase64);
      pdf.addFont('OpenSans-Bold.ttf', 'OpenSans', 'bold');
    }
  }

  private applyStyles(pdf: jsPDF, block: QrPosterBlock): void {
    pdf.setFont(this.fontsReady ? 'OpenSans' : 'helvetica', block.style?.fontWeight === 'bold' ? 'bold' : 'normal');
    pdf.setFontSize(block.style?.fontSize || 11);
    this.setColor(pdf, block.style?.color || '#000000');
  }

  private resolveBlockText(block: QrPosterBlock, context: QrPosterRenderContext, language: string): string {
    const localizedText = language === 'de' ? block.content?.de : block.content?.en;
    const rawText = localizedText || block.content?.text || block.content?.variable || '';
    if (block.type === 'variableText' || rawText.includes('{{')) {
      return this.variableResolver.resolveText(rawText, context);
    }
    return rawText;
  }

  private drawTextBlock(
    block: QrPosterBlock,
    options: QrPosterTextRenderOptions,
    wrapWidthOverride?: number
  ): void {
    const { pdf, context, language } = options;
    this.drawBlockBackground(block, pdf);
    const text = this.resolveBlockText(block, context, language);
    if (!text.trim()) {
      return;
    }
    const fontFamily = this.fontsReady ? 'OpenSans' : 'helvetica';
    const fontWeight = block.style?.fontWeight === 'bold' ? 'bold' : 'normal';
    pdf.setFont(fontFamily, fontWeight);
    this.setColor(pdf, block.style?.color || '#000000');
    const align = block.style?.textAlign || 'left';
    const textFit = normalizeQrPosterTextFit(block.style?.textFit, 'wrap');
    const horizontalPadding = this.getHorizontalTextPadding(block.width);
    const verticalPadding = Math.min(0.5, Math.max(0.16, block.height * 0.03));
    const availableWidth = Math.max(block.width - (horizontalPadding * 2), 1);
    const wrapWidth = textFit === 'wrap'
      ? Math.max(wrapWidthOverride ?? availableWidth, 1)
      : availableWidth;
    const availableHeight = Math.max(block.height - (verticalPadding * 2), 0.6);
    const fontSizeOriginal = block.style?.fontSize || 11;
    const minFontSize = Math.min(fontSizeOriginal, QrPosterTemplateRendererService.MIN_TEXT_FIT_FONT_SIZE_PT);
    const originalLines = textFit === 'singleLineShrink'
      ? [text]
      : this.splitPdfText(pdf, text, wrapWidth, fontSizeOriginal);
    const originalLongestWidth = this.getLongestLineWidth(pdf, originalLines, fontSizeOriginal);

    let fontSizeFinal = fontSizeOriginal;
    let visibleLines = [...originalLines];
    let didShrink = false;

    if (textFit === 'singleLineShrink') {
      fontSizeFinal = this.fitSingleLineFontSize(pdf, text, fontSizeOriginal, minFontSize, availableWidth, availableHeight);
      visibleLines = [text];
      didShrink = fontSizeFinal < fontSizeOriginal - 0.01;
    } else if (textFit === 'shrink') {
      const shrinkResult = this.fitWrappedTextFontSize(pdf, text, fontSizeOriginal, minFontSize, availableWidth, availableHeight);
      fontSizeFinal = shrinkResult.fontSize;
      visibleLines = shrinkResult.lines;
      didShrink = shrinkResult.didShrink;
    }

    const finalMetrics = this.getTextVerticalMetrics(fontSizeFinal);
    let clippedLines: string[] = [];
    let didClip = false;
    if (textFit !== 'singleLineShrink' && this.getOccupiedHeight(visibleLines.length, finalMetrics.lineHeight, finalMetrics.baselineOffset, finalMetrics.bottomGuard) > availableHeight) {
      const maxLines = this.getMaxVisibleLines(availableHeight, finalMetrics.lineHeight, finalMetrics.baselineOffset, finalMetrics.bottomGuard);
      clippedLines = visibleLines.slice(maxLines);
      visibleLines = visibleLines.slice(0, maxLines);
      didClip = clippedLines.length > 0;
    }

    pdf.setFontSize(fontSizeFinal);
    const totalTextHeight = this.getOccupiedHeight(visibleLines.length, finalMetrics.lineHeight, finalMetrics.baselineOffset, finalMetrics.bottomGuard);
    const preferredStartTop = block.y + Math.max(verticalPadding, (block.height - totalTextHeight) / 2);
    const minStartTop = block.y;
    const maxStartTop = Math.max(block.y, (block.y + block.height) - totalTextHeight);
    const startTop = Math.min(Math.max(preferredStartTop, minStartTop), maxStartTop);
    const startY = startTop + finalMetrics.baselineOffset;
    const textX = align === 'center'
      ? block.x + block.width / 2
      : align === 'right'
        ? block.x + block.width - horizontalPadding
        : block.x + horizontalPadding;

    visibleLines.forEach((line, index) => {
      const textY = startY + (index * finalMetrics.lineHeight);
      if (textY + finalMetrics.bottomGuard > block.y + block.height + 0.01) {
        return;
      }
      pdf.text(line, textX, textY, { align });
    });

    const finalLongestWidth = this.getLongestLineWidth(pdf, visibleLines, fontSizeFinal);
    const debugEnabled = options.debugTextRendering ?? this.debugQrPosterTextRendering;
    if (debugEnabled && (didShrink || didClip || (textFit === 'wrap' && originalLines.length > 1 && visibleLines.length < originalLines.length))) {
      const maxLines = this.getMaxVisibleLines(availableHeight, finalMetrics.lineHeight, finalMetrics.baselineOffset, finalMetrics.bottomGuard);
      this.debugTextFit(block, text, options, {
        textFit,
        fontSizeOriginal,
        fontSizeFinal,
        fontWeight,
        align,
        horizontalPadding,
        verticalPadding,
        availableWidth,
        availableHeight,
        pdfFontFamily: fontFamily,
        textWidthOriginal: originalLongestWidth,
        textWidthFinal: finalLongestWidth,
        splitLinesOriginal: originalLines,
        splitLinesFinal: visibleLines,
        lineCountOriginal: originalLines.length,
        lineCountFinal: visibleLines.length,
        maxLines,
        didShrink,
        didClip,
        clippedLines,
      });
    }
  }

  private fitSingleLineFontSize(
    pdf: jsPDF,
    text: string,
    initialFontSize: number,
    minFontSize: number,
    availableWidth: number,
    availableHeight: number
  ): number {
    const maxHeightFontSize = Math.max(
      minFontSize,
      Math.min(initialFontSize, availableHeight / (0.3528 * 1.04))
    );
    let nextFontSize = maxHeightFontSize;
    let measuredWidth = this.measurePdfTextWidth(pdf, text, nextFontSize);
    if (measuredWidth <= availableWidth) {
      return nextFontSize;
    }

    const proportionalFontSize = Math.max(minFontSize, nextFontSize * (availableWidth / Math.max(measuredWidth, 0.001)));
    nextFontSize = Math.min(nextFontSize, proportionalFontSize);
    measuredWidth = this.measurePdfTextWidth(pdf, text, nextFontSize);

    while (measuredWidth > availableWidth && nextFontSize > minFontSize) {
      nextFontSize = Math.max(minFontSize, nextFontSize - 0.2);
      measuredWidth = this.measurePdfTextWidth(pdf, text, nextFontSize);
    }

    return nextFontSize;
  }

  private fitWrappedTextFontSize(
    pdf: jsPDF,
    text: string,
    initialFontSize: number,
    minFontSize: number,
    availableWidth: number,
    availableHeight: number
  ): { fontSize: number; lines: string[]; didShrink: boolean } {
    let fontSize = initialFontSize;
    let lines = this.splitPdfText(pdf, text, availableWidth, fontSize);
    let metrics = this.getTextVerticalMetrics(fontSize);
    let occupiedHeight = this.getOccupiedHeight(lines.length, metrics.lineHeight, metrics.baselineOffset, metrics.bottomGuard);

    while (occupiedHeight > availableHeight && fontSize > minFontSize) {
      const shrinkRatio = availableHeight / Math.max(occupiedHeight, 0.001);
      const candidateFontSize = Math.max(minFontSize, Math.min(fontSize - 0.2, fontSize * Math.max(0.82, shrinkRatio)));
      if (candidateFontSize >= fontSize) {
        break;
      }
      fontSize = candidateFontSize;
      lines = this.splitPdfText(pdf, text, availableWidth, fontSize);
      metrics = this.getTextVerticalMetrics(fontSize);
      occupiedHeight = this.getOccupiedHeight(lines.length, metrics.lineHeight, metrics.baselineOffset, metrics.bottomGuard);
    }

    return {
      fontSize,
      lines,
      didShrink: fontSize < initialFontSize - 0.01,
    };
  }

  private splitPdfText(pdf: jsPDF, text: string, availableWidth: number, fontSize: number): string[] {
    pdf.setFontSize(fontSize);
    const lines = pdf.splitTextToSize(text, Math.max(availableWidth, 1)) as string[];
    return lines.length ? lines : [''];
  }

  private measurePdfTextWidth(pdf: jsPDF, text: string, fontSize: number): number {
    pdf.setFontSize(fontSize);
    return pdf.getTextWidth(text || ' ');
  }

  private getLongestLineWidth(pdf: jsPDF, lines: string[], fontSize: number): number {
    pdf.setFontSize(fontSize);
    return lines.reduce((maxWidth, line) => Math.max(maxWidth, pdf.getTextWidth(line || ' ')), 0);
  }

  private getHorizontalTextPadding(width: number): number {
    return Math.min(0.8, Math.max(0.2, width * 0.025));
  }

  private getTextVerticalMetrics(fontSize: number): { lineHeight: number; baselineOffset: number; bottomGuard: number } {
    const fontSizeMm = fontSize * 0.3528;
    return {
      lineHeight: fontSizeMm * 1.12,
      baselineOffset: fontSizeMm * 0.78,
      bottomGuard: fontSizeMm * 0.26,
    };
  }

  private getOccupiedHeight(lineCount: number, lineHeight: number, baselineOffset: number, bottomGuard: number): number {
    return baselineOffset + (Math.max(lineCount - 1, 0) * lineHeight) + bottomGuard;
  }

  private getMaxVisibleLines(availableHeight: number, lineHeight: number, baselineOffset: number, bottomGuard: number): number {
    const usableHeight = availableHeight - baselineOffset - bottomGuard;
    if (usableHeight <= 0) {
      return 1;
    }
    return Math.max(1, Math.floor(usableHeight / Math.max(lineHeight, 0.001)) + 1);
  }

  private debugTextFit(
    block: QrPosterBlock,
    text: string,
    options: QrPosterTextRenderOptions,
    metrics: QrPosterTextLayoutMetrics
  ): void {
    console.debug('[QR Poster][TextFit]', {
      blockId: block.id,
      blockType: block.type,
      text,
      textFit: metrics.textFit,
      fontSizeOriginal: metrics.fontSizeOriginal,
      fontSizeFinal: metrics.fontSizeFinal,
      fontWeight: metrics.fontWeight,
      blockWidth: block.width,
      blockHeight: block.height,
      horizontalPadding: metrics.horizontalPadding,
      verticalPadding: metrics.verticalPadding,
      availableWidth: metrics.availableWidth,
      availableHeight: metrics.availableHeight,
      pdfFontFamily: metrics.pdfFontFamily,
      fontsReady: this.fontsReady,
      textWidthOriginal: metrics.textWidthOriginal,
      textWidthFinal: metrics.textWidthFinal,
      splitLinesOriginal: metrics.splitLinesOriginal,
      splitLinesFinal: metrics.splitLinesFinal,
      lineCountOriginal: metrics.lineCountOriginal,
      lineCountFinal: metrics.lineCountFinal,
      maxLines: metrics.maxLines,
      didShrink: metrics.didShrink,
      didClip: metrics.didClip,
      clippedLines: metrics.clippedLines,
      align: metrics.align,
      templateId: options.template?.selectedTemplateId || '',
      renderSource: options.renderSource || '',
    });
  }

  private async drawImageBlock(
    block: QrPosterBlock,
    pdf: jsPDF,
    src: string,
    fit: 'contain' | 'cover' | 'stretch' = 'cover',
  ): Promise<void> {
    this.drawBlockBackground(block, pdf);
    const normalizedSrc = src || 'assets/scanafy_company_logo_croped.png';
    const img = await this.loadImage(normalizedSrc);
    if (!img) {
      return;
    }

    if (fit === 'stretch') {
      pdf.addImage(img, this.detectImageFormat(normalizedSrc), block.x, block.y, block.width, block.height);
      return;
    }

    if (fit === 'cover') {
      const coverDataUrl = this.createCoverImageDataUrl(img, block.width, block.height);
      if (coverDataUrl) {
        pdf.addImage(coverDataUrl, this.detectImageFormat(coverDataUrl), block.x, block.y, block.width, block.height);
        return;
      }
      pdf.addImage(img, this.detectImageFormat(normalizedSrc), block.x, block.y, block.width, block.height);
      return;
    }

    const containSize = this.getContainSize(block.width, block.height, img.width, img.height);
    const x = block.x + (block.width - containSize.width) / 2;
    const y = block.y + (block.height - containSize.height) / 2;
    pdf.addImage(img, this.detectImageFormat(normalizedSrc), x, y, containSize.width, containSize.height);
  }

  private async preloadTemplateIcons(
    template: QrPosterTemplate,
    context: QrPosterRenderContext
  ): Promise<Map<string, string | null>> {
    const result = new Map<string, string | null>();
    const keys = new Set<string>();
    for (const block of template.blocks || []) {
      if (block.hidden || (block.type !== 'icon' && block.type !== 'iconText' && block.type !== 'iconMultilineText')) {
        continue;
      }
      const iconKey = (block.content?.icon || '').trim().toLowerCase();
      if (!iconKey || iconKey === 'caret-down') {
        continue;
      }
      const color = ((block.type === 'iconText' || block.type === 'iconMultilineText' ? (block.style?.iconColor || block.style?.color) : block.style?.color) || '#111827').trim().toLowerCase();
      keys.add(`${iconKey}::${color}`);
    }

    await Promise.all(Array.from(keys).map(async (entry) => {
      const [iconKey, color] = entry.split('::');
      const dataUrl = await this.getIconPngDataUrl(iconKey, color || '#111827', context);
      result.set(entry, dataUrl);
    }));
    return result;
  }

  private async drawIconBlock(block: QrPosterBlock, pdf: jsPDF, iconDataUrls: Map<string, string | null>): Promise<void> {
    this.drawBlockBackground(block, pdf);
    const iconKey = (block.content?.icon || '').trim().toLowerCase();
    if (iconKey === 'caret-down') {
      this.drawCaretDown(pdf, block, block.style?.color || '#111827');
      return;
    }
    const color = block.style?.color || '#111827';
    const cacheKey = `${iconKey}::${color.trim().toLowerCase()}`;
    const pngDataUrl = iconDataUrls.get(cacheKey) || null;
    if (!pngDataUrl) {
      return;
    }
    const size = Math.min(block.width, block.height);
    const x = block.x + (block.width - size) / 2;
    const y = block.y + (block.height - size) / 2;
    try {
      pdf.addImage(pngDataUrl, 'PNG', x, y, size, size);
    } catch {
      return;
    }
  }

  private drawLineBlock(block: QrPosterBlock, pdf: jsPDF): void {
    const color = block.style?.borderColor || block.style?.color || '#000000';
    this.setColor(pdf, color, true);
    pdf.setFillColor(...this.parseColorToRgb(color));
    pdf.rect(block.x, block.y, Math.max(block.width, 0.1), Math.max(block.height, 0.1), 'F');
  }

  private drawRectangleBlock(block: QrPosterBlock, pdf: jsPDF): void {
    this.drawBlockBackground(block, pdf);
  }

  private drawIconTextBlock(
    block: QrPosterBlock,
    options: QrPosterTextRenderOptions,
    iconDataUrls: Map<string, string | null>
  ): void {
    this.drawBlockBackground(block, options.pdf);
    this.applyStyles(options.pdf, block);
    const iconKey = (block.content?.icon || '').trim().toLowerCase();
    const iconColor = block.style?.iconColor || block.style?.color || '#111827';
    const icon = iconKey && iconKey !== 'caret-down'
      ? (iconDataUrls.get(`${iconKey}::${iconColor.trim().toLowerCase()}`) || null)
      : '';
    const text = this.resolveBlockText({ ...block, type: 'variableText' }, options.context, options.language);
    const iconSize = Math.min(4.2, Math.max(block.height - 1, 1.5));
    const configuredGap = Math.max(0.5, Math.min(4, Number(block.style?.gap || 1.5)));
    const gap = (icon || iconKey === 'caret-down') ? configuredGap : 0;
    const align = block.style?.textAlign || 'left';
    const textWidth = options.pdf.getTextWidth(text);
    const rowWidth = Math.min(block.width, textWidth + iconSize + gap);
    const rowX = align === 'center'
      ? block.x + (block.width - rowWidth) / 2
      : align === 'right'
        ? block.x + block.width - rowWidth
        : block.x;
    const textBlockX = icon ? rowX + iconSize + gap : rowX;
    const textBlockWidth = icon ? Math.max(rowWidth - iconSize - gap + 1.6, 8) : block.width;
    const textBlock = {
      ...block,
      x: textBlockX,
      width: textBlockWidth,
      content: { text },
      type: 'text' as const,
      style: {
        ...(block.style || {}),
        textAlign: 'left' as const,
      },
    };

    if (iconKey === 'caret-down') {
      this.drawCaretDown(options.pdf, {
        ...block,
        x: rowX,
        y: block.y + 0.3,
        width: iconSize,
        height: iconSize,
      }, iconColor);
    } else if (icon) {
      options.pdf.addImage(icon, 'PNG', rowX, block.y + 0.3, iconSize, iconSize);
    }

    const textStartX = textBlockX + this.getHorizontalTextPadding(textBlockWidth);
    const physicalTextCapacity = Math.max((block.x + block.width) - textStartX, 0);
    const textFit = normalizeQrPosterTextFit(block.style?.textFit, 'wrap');
    const naturalRowWidth = textWidth + iconSize + gap;
    const rowWasCapped = naturalRowWidth > block.width;
    const oldAvailableWidth = Math.max(
      textBlockWidth - (this.getHorizontalTextPadding(textBlockWidth) * 2),
      1
    );
    const shouldCorrectFalseWrap = textFit === 'wrap'
      && !rowWasCapped
      && textWidth > oldAvailableWidth
      && textWidth <= physicalTextCapacity;
    const wrapWidthOverride = shouldCorrectFalseWrap ? textWidth : undefined;
    this.drawTextBlock(textBlock, options, wrapWidthOverride);
  }

  private drawIconMultilineTextBlock(
    block: QrPosterBlock,
    options: QrPosterTextRenderOptions,
    iconDataUrls: Map<string, string | null>
  ): void {
    this.drawBlockBackground(block, options.pdf);

    const { pdf, context, language } = options;
    const fontFamily = this.fontsReady ? 'OpenSans' : 'helvetica';
    const fontWeight = block.style?.fontWeight === 'bold' ? 'bold' : 'normal';
    pdf.setFont(fontFamily, fontWeight);
    this.setColor(pdf, block.style?.color || '#000000');

    const text = this.resolveBlockText({ ...block, type: 'variableText' }, context, language);
    if (!text.trim()) {
      return;
    }

    const textFit = normalizeQrPosterTextFit(block.style?.textFit, 'wrap');
    const horizontalPadding = Math.min(0.8, Math.max(0.2, block.width * 0.025));
    const verticalPadding = Math.min(0.5, Math.max(0.16, block.height * 0.03));
    const availableWidth = Math.max(block.width - (horizontalPadding * 2), 1);
    const availableHeight = Math.max(block.height - (verticalPadding * 2), 0.6);

    // Reserve icon space on the left so wrapping doesn't produce an overlong first line
    const iconSizeMm = Math.min(4.2, Math.max(block.height - 1, 1.5));
    const configuredGapMm = Math.max(0.5, Math.min(4, Number(block.style?.gap || 3)));
    const safetyPaddingMm = 1;
    const textMaxWidth = Math.max(block.width * 0.35, availableWidth - iconSizeMm - configuredGapMm - safetyPaddingMm);

    const fontSizeOriginal = block.style?.fontSize || 11;
    const minFontSize = Math.min(fontSizeOriginal, QrPosterTemplateRendererService.MIN_TEXT_FIT_FONT_SIZE_PT);

    const originalLines = textFit === 'singleLineShrink'
      ? [text]
      : this.splitPdfText(pdf, text, textMaxWidth, fontSizeOriginal);

    let fontSizeFinal = fontSizeOriginal;
    let visibleLines = [...originalLines];
    let didShrink = false;

    if (textFit === 'singleLineShrink') {
      fontSizeFinal = this.fitSingleLineFontSize(pdf, text, fontSizeOriginal, minFontSize, textMaxWidth, availableHeight);
      visibleLines = [text];
      didShrink = fontSizeFinal < fontSizeOriginal - 0.01;
    } else if (textFit === 'shrink') {
      const shrinkResult = this.fitWrappedTextFontSize(pdf, text, fontSizeOriginal, minFontSize, textMaxWidth, availableHeight);
      fontSizeFinal = shrinkResult.fontSize;
      visibleLines = shrinkResult.lines;
      didShrink = shrinkResult.didShrink;
    }

    const finalMetrics = this.getTextVerticalMetrics(fontSizeFinal);
    let didClip = false;
    if (textFit !== 'singleLineShrink' && this.getOccupiedHeight(visibleLines.length, finalMetrics.lineHeight, finalMetrics.baselineOffset, finalMetrics.bottomGuard) > availableHeight) {
      const maxLines = this.getMaxVisibleLines(availableHeight, finalMetrics.lineHeight, finalMetrics.baselineOffset, finalMetrics.bottomGuard);
      if (visibleLines.length > maxLines) {
        visibleLines = visibleLines.slice(0, maxLines);
        didClip = true;
      }
    }

    pdf.setFontSize(fontSizeFinal);

    // Icon setup
    const iconKey = (block.content?.icon || '').trim().toLowerCase();
    const iconColor = block.style?.iconColor || block.style?.color || '#111827';
    const configuredGap = Math.max(0.5, Math.min(4, Number(block.style?.gap || 3)));
    const icon = iconKey ? (iconDataUrls.get(`${iconKey}::${iconColor.trim().toLowerCase()}`) || null) : null;
    const iconSize = icon ? iconSizeMm : 0;
    const gap = icon ? configuredGap : 0;

    // Center text on block unless the template explicitly opts into
    // centering the complete icon-and-text composition.
    const centerX = block.x + block.width / 2;
    const pageSafeLeft = 5;
    const centerComposite = block.style?.textAlign === 'center'
      && block.style?.centerIconTextComposite === true
      && !!icon;
    const firstLineWidth = visibleLines.length > 0 ? pdf.getTextWidth(visibleLines[0]) : 0;
    const maxLineWidth = visibleLines.reduce(
      (maximum, line) => Math.max(maximum, pdf.getTextWidth(line)),
      0,
    );
    const combinedWidth = iconSize + gap + maxLineWidth;
    const combinedLeft = centerX - (combinedWidth / 2);
    const textColumnCenter = combinedLeft + iconSize + gap + (maxLineWidth / 2);
    const firstLineLeftX = centerX - firstLineWidth / 2;
    let iconX = centerComposite
      ? combinedLeft
      : firstLineLeftX - gap - iconSize;

    if (!centerComposite && iconX < pageSafeLeft) {
      iconX = pageSafeLeft;
    }
    if (!centerComposite && iconX > firstLineLeftX - iconSize && iconX > pageSafeLeft) {
      iconX = Math.max(pageSafeLeft, firstLineLeftX - iconSize - 1);
    }

    // Total text height and vertical positioning
    const totalTextHeight = this.getOccupiedHeight(visibleLines.length, finalMetrics.lineHeight, finalMetrics.baselineOffset, finalMetrics.bottomGuard);
    const preferredStartTop = block.y + Math.max(verticalPadding, (block.height - totalTextHeight) / 2);
    const minStartTop = block.y;
    const maxStartTop = Math.max(block.y, (block.y + block.height) - totalTextHeight);
    const startTop = Math.min(Math.max(preferredStartTop, minStartTop), maxStartTop);
    const startY = startTop + finalMetrics.baselineOffset;

    // Draw icon aligned with first text line baseline
    const firstLineTop = startTop;
    const iconY = firstLineTop + (finalMetrics.baselineOffset - iconSize) * 0.7;
    if (icon) {
      pdf.addImage(icon, 'PNG', iconX, Math.max(block.y, iconY), iconSize, iconSize);
    }

    // Preserve historical text-only centering unless the template explicitly
    // opts into centering the complete icon-and-text composition.
    visibleLines.forEach((line, index) => {
      const textY = startY + (index * finalMetrics.lineHeight);
      if (textY + finalMetrics.bottomGuard > block.y + block.height + 0.01) {
        return;
      }
      pdf.text(line, centerComposite ? textColumnCenter : centerX, textY, { align: 'center' });
    });
  }

  private drawEmergencyNumbersBlock(
    block: QrPosterBlock,
    options: QrPosterTextRenderOptions
  ): void {
    this.drawBlockBackground(block, options.pdf);
    this.applyStyles(options.pdf, block);
    const rows = block.content?.rows || [];
    const fontSize = block.style?.fontSize || 9;
    const lineHeight = fontSize * 0.3528 * 1.35;
    let currentY = block.y + 4;
    const usableWidth = block.width - 4;

    rows.forEach((row) => {
      const label = this.variableResolver.resolveText(row.label, options.context);
      const value = this.variableResolver.resolveText(row.value, options.context);
      const description = this.variableResolver.resolveText(row.description || '', options.context);
      const labelLines = options.pdf.splitTextToSize(label, Math.max(usableWidth - 28, 18)) as string[];
      const valueLines = options.pdf.splitTextToSize(value, 24) as string[];
      const firstRowHeight = Math.max(labelLines.length, valueLines.length) * lineHeight;

      options.pdf.setFont(this.fontsReady ? 'OpenSans' : 'helvetica', 'bold');
      labelLines.forEach((line, index) => {
        options.pdf.text(line, block.x + 2, currentY + (index * lineHeight));
      });
      options.pdf.setFont(this.fontsReady ? 'OpenSans' : 'helvetica', 'bold');
      valueLines.forEach((line, index) => {
        options.pdf.text(line, block.x + block.width - 2, currentY + (index * lineHeight), { align: 'right' });
      });
      currentY += firstRowHeight;
      if (description) {
        options.pdf.setFont(this.fontsReady ? 'OpenSans' : 'helvetica', 'normal');
        const descriptionLines = options.pdf.splitTextToSize(description, usableWidth) as string[];
        descriptionLines.forEach((line) => {
          options.pdf.text(line, block.x + 2, currentY);
          currentY += lineHeight;
        });
      }
      currentY += 1.6;
    });
  }

  private drawFooter(pdf: jsPDF, x: number, y: number, width: number, height: number, block?: QrPosterBlock): void {
    const bgHex = block?.style?.backgroundColor || '#d9d9d9';
    const fillColor = this.parseColor(bgHex);
    if (fillColor.a > 0) {
      this.setColor(pdf, bgHex);
      pdf.setFillColor(fillColor.r, fillColor.g, fillColor.b);
      pdf.rect(x, y, width, height, 'F');
    }
    pdf.setFont(this.fontsReady ? 'OpenSans' : 'helvetica', 'normal');
    pdf.setFontSize(Math.min(14, height * 0.9));
    if (block?.style?.color) {
      this.setColor(pdf, block.style.color, true);
      pdf.setTextColor(...this.parseColorToRgb(block.style.color));
    } else {
      const rgb = this.parseColorToRgb(bgHex);
      const luminance = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;
      pdf.setTextColor(luminance < 0.5 ? 255 : 0, luminance < 0.5 ? 255 : 0, luminance < 0.5 ? 255 : 0);
    }
    const baseline = y + height / 2 + (pdf.getFontSize() * 0.3528) / 4;
    pdf.text('www.scanafy.de', x + width / 2, baseline, { align: 'center' });
  }

  private drawBlockBackground(block: QrPosterBlock, pdf: jsPDF): void {
    const fillColor = block.style?.backgroundColor ? this.parseColor(block.style.backgroundColor) : null;
    const hasFill = !!fillColor && fillColor.a > 0;
    const borderWidth = Number(block.style?.borderWidth || 0);
    const borderColor = block.style?.borderColor ? this.parseColor(block.style.borderColor) : null;
    const hasBorder = borderWidth > 0 && !!borderColor && borderColor.a > 0;

    if (hasFill) {
      this.setColor(pdf, block.style?.backgroundColor || '#ffffff');
      pdf.setFillColor(fillColor.r, fillColor.g, fillColor.b);
    }

    if (hasBorder) {
      this.setColor(pdf, block.style?.borderColor || '#000000', true);
      pdf.setLineWidth(borderWidth);
    }

    if (hasFill || hasBorder) {
      const mode = hasFill && hasBorder ? 'FD' : hasFill ? 'F' : 'S';
      const radius = block.style?.borderRadius || 0;
      if (radius > 0) {
        pdf.roundedRect(block.x, block.y, block.width, block.height, radius, radius, mode);
      } else {
        pdf.rect(block.x, block.y, block.width, block.height, mode);
      }
    }
  }

  private setColor(pdf: jsPDF, hex: string, stroke = false): void {
    const [r, g, b] = this.parseColorToRgb(hex);
    if (stroke) {
      pdf.setDrawColor(r, g, b);
      return;
    }
    pdf.setTextColor(r, g, b);
  }

  private parseColor(value: string): ParsedPdfColor {
    const input = (value || '').trim();
    const rgbaMatch = input.match(/^rgba?\(([^)]+)\)$/i);
    if (rgbaMatch) {
      const parts = rgbaMatch[1].split(',').map((part) => Number.parseFloat(part.trim()));
      if (parts.length >= 3 && parts.slice(0, 3).every((part) => Number.isFinite(part))) {
        return {
          r: Math.max(0, Math.min(255, Math.round(parts[0]))),
          g: Math.max(0, Math.min(255, Math.round(parts[1]))),
          b: Math.max(0, Math.min(255, Math.round(parts[2]))),
          a: parts.length >= 4 && Number.isFinite(parts[3]) ? Math.max(0, Math.min(1, parts[3])) : 1,
        };
      }
    }

    const normalized = input.replace('#', '');
    if (/^[0-9a-fA-F]{3}$/.test(normalized)) {
      const [r, g, b] = normalized.split('').map((part) => parseInt(part + part, 16));
      return { r, g, b, a: 1 };
    }
    if (/^[0-9a-fA-F]{4}$/.test(normalized)) {
      const [r, g, b, a] = normalized.split('').map((part) => parseInt(part + part, 16));
      return { r, g, b, a: a / 255 };
    }
    if (/^[0-9a-fA-F]{6}$/.test(normalized)) {
      return {
        r: parseInt(normalized.slice(0, 2), 16),
        g: parseInt(normalized.slice(2, 4), 16),
        b: parseInt(normalized.slice(4, 6), 16),
        a: 1,
      };
    }
    if (/^[0-9a-fA-F]{8}$/.test(normalized)) {
      return {
        r: parseInt(normalized.slice(0, 2), 16),
        g: parseInt(normalized.slice(2, 4), 16),
        b: parseInt(normalized.slice(4, 6), 16),
        a: parseInt(normalized.slice(6, 8), 16) / 255,
      };
    }

    const valueHex = normalized.length === 3
      ? normalized.split('').map((part) => part + part).join('')
      : normalized.padEnd(6, '0').slice(0, 6);
    const int = Number.parseInt(valueHex, 16);
    if (!Number.isFinite(int)) {
      return { r: 0, g: 0, b: 0, a: 1 };
    }
    return {
      r: (int >> 16) & 255,
      g: (int >> 8) & 255,
      b: int & 255,
      a: 1,
    };
  }

  private parseColorToRgb(value: string): [number, number, number] {
    const { r, g, b } = this.parseColor(value);
    return [r, g, b];
  }

  private async loadImage(src: string): Promise<HTMLImageElement | null> {
    return new Promise((resolve) => {
      const img = new Image();
      if (!src.startsWith('data:') && !src.startsWith('blob:')) {
        img.crossOrigin = 'anonymous';
      }
      img.onload = () => resolve(img);
      img.onerror = () => {
        if (src !== 'assets/scanafy_company_logo_croped.png') {
          this.loadImage('assets/scanafy_company_logo_croped.png').then(resolve);
          return;
        }
        resolve(null);
      };
      img.src = src;
    });
  }

  private detectImageFormat(src: string): 'PNG' | 'JPEG' {
    return src.startsWith('data:image/jpeg') || src.startsWith('data:image/jpg') ? 'JPEG' : 'PNG';
  }

  private async getIconPngDataUrl(iconType: string, color: string, context?: QrPosterRenderContext): Promise<string | null> {
    const normalizedKey = (iconType || '').trim().toLowerCase();
    const normalizedColor = this.normalizeIconColor(color);
    const cacheKey = `${normalizedKey}::${normalizedColor}`;
    if (this.iconPngCache.has(cacheKey)) {
      return this.iconPngCache.get(cacheKey) || null;
    }
    const contextIconSrc = this.getContextIconSource(normalizedKey, context);
    if (contextIconSrc) {
      const contextDataUrl = await this.convertImageSourceToDataUrl(contextIconSrc);
      if (contextDataUrl) {
        if (this.normalizeIconColor(context?.themeColor) === normalizedColor) {
          this.iconPngCache.set(cacheKey, contextDataUrl);
          return contextDataUrl;
        }

        try {
          const recoloredIconDataUrl = await this.recolorContextIcon(contextDataUrl, normalizedColor);
          if (recoloredIconDataUrl) {
            this.iconPngCache.set(cacheKey, recoloredIconDataUrl);
            return recoloredIconDataUrl;
          }
        } catch {
          // Keep the original context icon visible below, but do not cache it as a custom-color icon.
        }

        // The context image has the theme color, so it must not poison the custom-color cache entry.
        return contextDataUrl;
      }
    }
    if (!getQrPosterIconDefinition(normalizedKey)) {
      if (normalizedKey !== 'environment') {
        return this.getIconPngDataUrl('environment', color, context);
      }
      return null;
    }
    try {
      const svg = await firstValueFrom(this.iconService.getRenderedContent(getQrPosterPreviewIconType(normalizedKey)));
      const cloned = svg.cloneNode(true) as SVGElement;
      cloned.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      if (!cloned.getAttribute('viewBox')) {
        cloned.setAttribute('viewBox', '0 0 1024 1024');
      }
      cloned.setAttribute('width', '1024');
      cloned.setAttribute('height', '1024');
      cloned.setAttribute('fill', normalizedColor);
      cloned.setAttribute('color', normalizedColor);
      cloned.querySelectorAll('path,circle,rect,polygon,polyline,line,ellipse').forEach((node) => {
        node.setAttribute('fill', normalizedColor);
        node.setAttribute('stroke', normalizedColor);
      });
      const svgText = new XMLSerializer().serializeToString(cloned);
      const blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      try {
        const image = await this.loadImage(url);
        if (!image) {
          return null;
        }
        const canvas = document.createElement('canvas');
        canvas.width = 128;
        canvas.height = 128;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return null;
        }
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/png');
        this.iconPngCache.set(cacheKey, dataUrl);
        return dataUrl;
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch {
      if (normalizedKey !== 'environment') {
        const fallback = await this.getIconPngDataUrl('environment', color, context);
        this.iconPngCache.set(cacheKey, fallback);
        return fallback;
      }
      return null;
    }
  }

  private getContextIconSource(iconKey: string, context?: QrPosterRenderContext): string {
    if (!context?.icons) {
      return '';
    }
    return context.icons[iconKey as keyof QrPosterRenderContext['icons']] || '';
  }

  private normalizeIconColor(color: string | null | undefined): string {
    return (color || '#111827').trim().toLowerCase();
  }

  private async recolorContextIcon(src: string, color: string): Promise<string | null> {
    const image = await this.loadImage(src);
    if (!image) {
      return null;
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, image.naturalWidth || image.width || 128);
    canvas.height = Math.max(1, image.naturalHeight || image.height || 128);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }

    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    const parsedColor = this.parseColor(color);
    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = `rgba(${parsedColor.r}, ${parsedColor.g}, ${parsedColor.b}, ${parsedColor.a})`;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/png');
  }

  private async convertImageSourceToDataUrl(src: string): Promise<string | null> {
    if (!src) {
      return null;
    }
    if (src.startsWith('data:image/')) {
      return src;
    }
    const image = await this.loadImage(src);
    if (!image) {
      return null;
    }
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }
    ctx.drawImage(image, 0, 0, 128, 128);
    return canvas.toDataURL('image/png');
  }

  private getContainSize(
    frameWidth: number,
    frameHeight: number,
    sourceWidth: number,
    sourceHeight: number,
  ): { width: number; height: number } {
    const ratio = Math.min(frameWidth / sourceWidth, frameHeight / sourceHeight);
    return {
      width: sourceWidth * ratio,
      height: sourceHeight * ratio,
    };
  }

  private createCoverImageDataUrl(image: HTMLImageElement, widthMm: number, heightMm: number): string | null {
    try {
      const canvas = document.createElement('canvas');
      const targetWidth = Math.max(32, Math.round(widthMm * 10));
      const targetHeight = Math.max(32, Math.round(heightMm * 10));
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const context = canvas.getContext('2d');
      if (!context) {
        return null;
      }

      const scale = Math.max(targetWidth / image.width, targetHeight / image.height);
      const drawWidth = image.width * scale;
      const drawHeight = image.height * scale;
      const offsetX = (targetWidth - drawWidth) / 2;
      const offsetY = (targetHeight - drawHeight) / 2;
      context.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);
      return canvas.toDataURL(this.detectImageFormat(image.src) === 'JPEG' ? 'image/jpeg' : 'image/png');
    } catch {
      return null;
    }
  }

  private drawCaretDown(pdf: jsPDF, block: QrPosterBlock, color: string): void {
    const [r, g, b] = this.parseColorToRgb(color);
    const centerX = block.x + block.width / 2;
    const topY = block.y + (block.height * 0.2);
    const bottomY = block.y + (block.height * 0.8);
    pdf.setFillColor(r, g, b);
    pdf.triangle(
      centerX - (block.width * 0.36),
      topY,
      centerX + (block.width * 0.36),
      topY,
      centerX,
      bottomY,
      'F',
    );
  }

  private async withBlockRotation(pdf: jsPDF, block: QrPosterBlock, render: () => Promise<void> | void): Promise<void> {
    const rotation = this.normalizeRotation(block.rotation || 0);
    if (!rotation) {
      await render();
      return;
    }
    const matrixCtor = (pdf as jsPDF & { Matrix?: new (a: number, b: number, c: number, d: number, e: number, f: number) => Matrix }).Matrix;
    if (!matrixCtor) {
      await render();
      return;
    }
    const centerX = block.x + (block.width / 2);
    const centerY = block.y + (block.height / 2);
    const radians = rotation * (Math.PI / 180);
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const matrix = new matrixCtor(
      cos,
      sin,
      -sin,
      cos,
      centerX - (cos * centerX) + (sin * centerY),
      centerY - (sin * centerX) - (cos * centerY),
    );
    pdf.saveGraphicsState();
    pdf.setCurrentTransformationMatrix(matrix);
    try {
      await render();
    } finally {
      pdf.restoreGraphicsState();
    }
  }

  private normalizeRotation(value: number): number {
    const normalized = Number.isFinite(value) ? value : 0;
    const wrapped = ((normalized % 360) + 360) % 360;
    return Number((wrapped > 180 ? wrapped - 360 : wrapped).toFixed(2));
  }
}
